import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { Declarant, Role, User } from '@e-origin/shared';

import { useAdminCreateUser, useAdminEditUser } from '@admin/hooks';
import { passwordSchema } from '@validators';

const createUserSchema = z.object({
  email: z.string().email({ message: 'Email address is required' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  password: passwordSchema,
  role: z.string().min(1, { message: 'Role is required' }),
  declarant: z.string().min(1, { message: 'Declarant is required' }),
  isSupervisor: z.boolean().optional(),
});

const editUserSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  deleted: z.boolean().optional(),
  visible: z.boolean().optional(),
  role: z.string().min(1, { message: 'Role is required' }),
  declarant: z.string().min(1, { message: 'Declarant is required' }),
  isSupervisor: z.boolean().optional(),
});

export const useEditUserFormInitializer = ({ user }) => {
  const createUserMutation = useAdminCreateUser();
  const editUserMutation = useAdminEditUser();

  const onSubmit = async (values) => {
    if (values._id) {
      return await editUserMutation.mutateAsync({
        params: { userId: values._id },
        body: {
          firstName: values.firstName,
          lastName: values.lastName,
          deleted: values.deleted,
          visible: values.visible,
          role: values.role,
          declarant: values.declarant,
          isSupervisor: values.isSupervisor,
        },
      });
    } else {
      return await createUserMutation.mutateAsync({
        body: {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password,
          role: values.role,
          declarant: values.declarant,
          isSupervisor: values.isSupervisor,
        },
      });
    }
  };

  const buildInitialValues = (user: User | undefined) => ({
    _id: user?._id,
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    deleted: user?.deleted || false,
    visible: user?.visible !== false,
    password: '',
    role: (user?.role as unknown as Role)?._id || '',
    declarant: (user?.declarant as unknown as Declarant)?._id || '',
    isSupervisor: user?.isSupervisor || false,
  });

  const form = useForm({
    initialValues: buildInitialValues(user),
    validate: zodResolver(user ? editUserSchema : createUserSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(buildInitialValues(user));
  }, [user]);

  return {
    form,
    onSubmit,
    isSubmitting: createUserMutation.isPending || editUserMutation.isPending,
  };
};
